import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createCreatorCrm,
  createCreatorCrmActivity,
  getCreatorCrm,
  listCreatorCrm,
  listCreatorCrmActivities,
  updateCreatorCrm,
} from "../services/creatorCrmService";

function norm(value) {
  return String(value ?? "").trim();
}

function getCreatorRowKey(row) {
  const explicit = norm(row?.creatorKey);
  if (explicit) return explicit;

  const creatorId = norm(
    row?.creatorId ||
      row?.influencerId ||
      row?.id ||
      row?._id
  );
  if (creatorId) return `id:${creatorId.toLowerCase()}`;

  const handle = norm(
    row?.handle ||
      row?.username ||
      row?.creatorHandle ||
      row?.crmHandle
  )
    .replace(/^@+/, "")
    .toLowerCase();

  if (handle) return `handle:${handle}`;

  const name = norm(
    row?.displayName ||
      row?.full_name ||
      row?.name
  ).toLowerCase();

  if (name) return `name:${name}`;

  return "";
}

function normalizeCrmStage(stage) {
  const value = norm(stage).toLowerCase();

  if (
    [
      "new",
      "contacted",
      "replied",
      "negotiating",
      "scheduled",
      "live",
      "completed",
      "not_now",
    ].includes(value)
  ) {
    return value;
  }

  return "new";
}

function normalizeRow(row) {
  const creatorKey = getCreatorRowKey(row);

  return {
    ...row,
    creatorKey,
    crmStage: normalizeCrmStage(
      row?.crmStage ||
        row?.crmStatus ||
        row?.stage ||
        row?.status
    ),
    crmStatus: normalizeCrmStage(
      row?.crmStatus ||
        row?.crmStage ||
        row?.status ||
        row?.stage
    ),
    nextStep: norm(row?.nextStep || row?.nextAction),
    notes: norm(row?.notes || row?.notesPreview),
    notesPreview: norm(row?.notesPreview || row?.notes),
    lastContactedAt: norm(row?.lastContactedAt || row?.lastTouchedAt),
    lastTouchedAt: norm(row?.lastTouchedAt || row?.lastContactedAt),
    offerId: norm(row?.offerId || row?.crmOfferId),
    crmOfferId: norm(row?.crmOfferId || row?.offerId),
  };
}

function normalizeActivity(activity) {
  return {
    ...activity,
    activityId: norm(
      activity?.activityId ||
        activity?.id ||
        activity?._id
    ),
    type: norm(activity?.type || activity?.activityType || "note"),
    message: norm(activity?.message || activity?.text || activity?.notes),
    createdAt: norm(activity?.createdAt || activity?.timestamp || activity?.occurredAt),
    actorName: norm(activity?.actorName || activity?.createdBy || activity?.userName),
  };
}

function upsertRow(items, incomingRow) {
  const nextRow = normalizeRow(incomingRow);
  const key = getCreatorRowKey(nextRow);

  if (!key) return items;

  const index = items.findIndex((item) => getCreatorRowKey(item) === key);

  if (index < 0) {
    return [nextRow, ...items];
  }

  const clone = [...items];
  clone[index] = {
    ...clone[index],
    ...nextRow,
  };
  return clone;
}

function mergeDetail(existing, incoming) {
  if (!incoming || typeof incoming !== "object") {
    return existing || null;
  }

  return {
    ...(existing || {}),
    ...normalizeRow(incoming),
  };
}

function prependActivity(existingItems, incomingActivity) {
  const normalized = normalizeActivity(incomingActivity || {});
  const activityId = norm(normalized.activityId);

  if (!activityId) {
    return [normalized, ...(Array.isArray(existingItems) ? existingItems : [])];
  }

  const items = Array.isArray(existingItems) ? [...existingItems] : [];
  const index = items.findIndex(
    (item) => norm(item?.activityId || item?.id || item?._id) === activityId
  );

  if (index >= 0) {
    items[index] = {
      ...items[index],
      ...normalized,
    };
    return items;
  }

  return [normalized, ...items];
}

function extractCampaignId(input = {}) {
  return norm(
    input?.campaignId ||
      input?.params?.campaignId ||
      input?.payload?.campaignId
  );
}

function extractCreatorKey(input = {}) {
  return norm(
    input?.creatorKey ||
      input?.payload?.creatorKey
  );
}

export const listCreatorsCrmAsync = createAsyncThunk(
  "creatorsCrm/list",
  async ({ params = {} } = {}, { extra, rejectWithValue }) => {
    try {
      const campaignId = norm(params?.campaignId);
      if (!campaignId) {
        return rejectWithValue("campaignId is required to load creator CRM.");
      }

      const token = await extra.getAccessToken?.();

      return await listCreatorCrm({
        campaignId,
        token: token || "",
        params,
      });
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to load creator CRM");
    }
  }
);

export const getCreatorCrmAsync = createAsyncThunk(
  "creatorsCrm/get",
  async ({ campaignId, creatorKey } = {}, { extra, rejectWithValue }) => {
    try {
      const safeCampaignId = norm(campaignId);
      const safeCreatorKey = norm(creatorKey);

      if (!safeCampaignId) {
        return rejectWithValue({
          creatorKey: safeCreatorKey,
          message: "campaignId is required to load creator CRM details.",
        });
      }

      if (!safeCreatorKey) {
        return rejectWithValue({
          creatorKey: safeCreatorKey,
          message: "creatorKey is required to load creator CRM details.",
        });
      }

      const token = await extra.getAccessToken?.();
      const item = await getCreatorCrm({
        campaignId: safeCampaignId,
        creatorKey: safeCreatorKey,
        token: token || "",
      });

      return {
        creatorKey: safeCreatorKey,
        item: normalizeRow(item || { creatorKey: safeCreatorKey }),
      };
    } catch (error) {
      return rejectWithValue({
        creatorKey: norm(creatorKey),
        message: error?.message || "Failed to load creator CRM details",
      });
    }
  }
);

export const createCreatorCrmAsync = createAsyncThunk(
  "creatorsCrm/create",
  async ({ campaignId, creatorKey, payload = {} } = {}, { extra, rejectWithValue }) => {
    try {
      const safeCampaignId = extractCampaignId({ campaignId, payload });
      const safeCreatorKey = extractCreatorKey({ creatorKey, payload });

      if (!safeCampaignId) {
        return rejectWithValue({
          creatorKey: safeCreatorKey,
          message: "campaignId is required to create creator CRM.",
        });
      }

      if (!safeCreatorKey) {
        return rejectWithValue({
          creatorKey: safeCreatorKey,
          message: "creatorKey is required to create creator CRM.",
        });
      }

      const token = await extra.getAccessToken?.();

      const item = await createCreatorCrm({
        campaignId: safeCampaignId,
        creatorKey: safeCreatorKey,
        token: token || "",
        payload,
      });

      return {
        creatorKey: safeCreatorKey,
        item: normalizeRow(item || { creatorKey: safeCreatorKey, ...payload }),
      };
    } catch (error) {
      return rejectWithValue({
        creatorKey: extractCreatorKey({ creatorKey, payload }),
        message: error?.message || "Failed to create creator CRM",
      });
    }
  }
);

export const updateCreatorCrmAsync = createAsyncThunk(
  "creatorsCrm/update",
  async ({ campaignId, creatorKey, payload = {} } = {}, { extra, rejectWithValue }) => {
    try {
      const safeCampaignId = extractCampaignId({ campaignId, payload });
      const safeCreatorKey = extractCreatorKey({ creatorKey, payload });

      if (!safeCampaignId) {
        return rejectWithValue({
          creatorKey: safeCreatorKey,
          message: "campaignId is required to update creator CRM.",
        });
      }

      if (!safeCreatorKey) {
        return rejectWithValue({
          creatorKey: safeCreatorKey,
          message: "creatorKey is required to update creator CRM.",
        });
      }

      const token = await extra.getAccessToken?.();
      const item = await updateCreatorCrm({
        campaignId: safeCampaignId,
        creatorKey: safeCreatorKey,
        token: token || "",
        payload,
      });

      return {
        creatorKey: safeCreatorKey,
        item: normalizeRow(item || { creatorKey: safeCreatorKey, ...payload }),
      };
    } catch (error) {
      return rejectWithValue({
        creatorKey: extractCreatorKey({ creatorKey, payload }),
        message: error?.message || "Failed to update creator CRM",
      });
    }
  }
);

export const listCreatorCrmActivitiesAsync = createAsyncThunk(
  "creatorsCrm/listActivities",
  async ({ campaignId, creatorKey, params = {} } = {}, { extra, rejectWithValue }) => {
    try {
      const safeCampaignId = norm(campaignId);
      const safeCreatorKey = norm(creatorKey);

      if (!safeCampaignId) {
        return rejectWithValue({
          creatorKey: safeCreatorKey,
          message: "campaignId is required to load creator CRM activities.",
        });
      }

      if (!safeCreatorKey) {
        return rejectWithValue({
          creatorKey: safeCreatorKey,
          message: "creatorKey is required to load creator CRM activities.",
        });
      }

      const token = await extra.getAccessToken?.();

      const response = await listCreatorCrmActivities({
        campaignId: safeCampaignId,
        creatorKey: safeCreatorKey,
        token: token || "",
        params,
      });

      const items = Array.isArray(response?.items)
        ? response.items.map(normalizeActivity)
        : [];

      return {
        creatorKey: safeCreatorKey,
        items,
        total: Number(response?.total ?? items.length) || items.length,
        unavailable: Boolean(response?.unavailable),
      };
    } catch (error) {
      return rejectWithValue({
        creatorKey: norm(creatorKey),
        message: error?.message || "Failed to load creator CRM activities",
      });
    }
  }
);

export const createCreatorCrmActivityAsync = createAsyncThunk(
  "creatorsCrm/createActivity",
  async ({ campaignId, creatorKey, payload = {} } = {}, { extra, rejectWithValue }) => {
    try {
      const safeCampaignId = extractCampaignId({ campaignId, payload });
      const safeCreatorKey = extractCreatorKey({ creatorKey, payload });

      if (!safeCampaignId) {
        return rejectWithValue({
          creatorKey: safeCreatorKey,
          message: "campaignId is required to create creator CRM activity.",
        });
      }

      if (!safeCreatorKey) {
        return rejectWithValue({
          creatorKey: safeCreatorKey,
          message: "creatorKey is required to create creator CRM activity.",
        });
      }

      const token = await extra.getAccessToken?.();
      const item = await createCreatorCrmActivity({
        campaignId: safeCampaignId,
        creatorKey: safeCreatorKey,
        token: token || "",
        payload,
      });

      return {
        creatorKey: safeCreatorKey,
        activity: normalizeActivity(item || payload),
      };
    } catch (error) {
      return rejectWithValue({
        creatorKey: extractCreatorKey({ creatorKey, payload }),
        message: error?.message || "Failed to create creator CRM activity",
      });
    }
  }
);

const initialState = {
  items: [],
  total: 0,
  loading: false,
  error: "",
  savingByKey: {},
  updateErrorByKey: {},

  detailByKey: {},
  detailLoadingByKey: {},
  detailErrorByKey: {},

  activitiesByKey: {},
  activitiesLoadingByKey: {},
  activitiesErrorByKey: {},
  creatingActivityByKey: {},
};

const creatorsCrmSlice = createSlice({
  name: "creatorsCrm",
  initialState,
  reducers: {
    clearCreatorsCrmError(state) {
      state.error = "";
    },
    clearCreatorCrmUpdateError(state, action) {
      const key = norm(action.payload);
      if (key) {
        delete state.updateErrorByKey[key];
      }
    },
    clearCreatorCrmDetailError(state, action) {
      const key = norm(action.payload);
      if (key) {
        delete state.detailErrorByKey[key];
      }
    },
    clearCreatorCrmActivitiesError(state, action) {
      const key = norm(action.payload);
      if (key) {
        delete state.activitiesErrorByKey[key];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(listCreatorsCrmAsync.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(listCreatorsCrmAsync.fulfilled, (state, action) => {
        const items = Array.isArray(action.payload?.items)
          ? action.payload.items.map(normalizeRow)
          : [];

        state.loading = false;
        state.error = "";
        state.items = items;
        state.total =
          Number(action.payload?.total ?? items.length) || items.length;
      })
      .addCase(listCreatorsCrmAsync.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || action.error?.message || "Failed to load creator CRM";
      })

      .addCase(getCreatorCrmAsync.pending, (state, action) => {
        const key = norm(action.meta.arg?.creatorKey);
        if (key) {
          state.detailLoadingByKey[key] = true;
          delete state.detailErrorByKey[key];
        }
      })
      .addCase(getCreatorCrmAsync.fulfilled, (state, action) => {
        const key = norm(action.payload?.creatorKey);
        if (!key) return;

        state.detailLoadingByKey[key] = false;
        delete state.detailErrorByKey[key];
        state.detailByKey[key] = mergeDetail(
          state.detailByKey[key],
          action.payload?.item
        );
        state.items = upsertRow(state.items, action.payload?.item || {});
        state.total = Math.max(Number(state.total || 0), state.items.length);
      })
      .addCase(getCreatorCrmAsync.rejected, (state, action) => {
        const key = norm(action.payload?.creatorKey || action.meta.arg?.creatorKey);
        const message =
          action.payload?.message ||
          action.error?.message ||
          "Failed to load creator CRM details";

        if (key) {
          state.detailLoadingByKey[key] = false;
          state.detailErrorByKey[key] = message;
        } else {
          state.error = message;
        }
      })

      .addCase(createCreatorCrmAsync.pending, (state, action) => {
        const key = extractCreatorKey({
          creatorKey: action.meta.arg?.creatorKey,
          payload: action.meta.arg?.payload,
        });

        if (key) {
          state.savingByKey[key] = true;
          delete state.updateErrorByKey[key];
        }
      })
      .addCase(createCreatorCrmAsync.fulfilled, (state, action) => {
        const key = norm(action.payload?.creatorKey);
        if (key) {
          delete state.savingByKey[key];
          delete state.updateErrorByKey[key];
          state.detailByKey[key] = mergeDetail(
            state.detailByKey[key],
            action.payload?.item
          );
        }

        state.error = "";
        state.items = upsertRow(state.items, action.payload?.item || {});
        state.total = Math.max(Number(state.total || 0), state.items.length);
      })
      .addCase(createCreatorCrmAsync.rejected, (state, action) => {
        const key = norm(
          action.payload?.creatorKey ||
            action.meta.arg?.creatorKey ||
            action.meta.arg?.payload?.creatorKey
        );
        const message =
          action.payload?.message ||
          action.error?.message ||
          "Failed to create creator CRM";

        if (key) {
          delete state.savingByKey[key];
          state.updateErrorByKey[key] = message;
        } else {
          state.error = message;
        }
      })

      .addCase(updateCreatorCrmAsync.pending, (state, action) => {
        const key = extractCreatorKey({
          creatorKey: action.meta.arg?.creatorKey,
          payload: action.meta.arg?.payload,
        });

        if (key) {
          state.savingByKey[key] = true;
          delete state.updateErrorByKey[key];
        }
      })
      .addCase(updateCreatorCrmAsync.fulfilled, (state, action) => {
        const key = norm(action.payload?.creatorKey);
        if (key) {
          delete state.savingByKey[key];
          delete state.updateErrorByKey[key];
          state.detailByKey[key] = mergeDetail(
            state.detailByKey[key],
            action.payload?.item
          );
        }

        state.error = "";
        state.items = upsertRow(state.items, action.payload?.item || {});
        state.total = Math.max(Number(state.total || 0), state.items.length);
      })
      .addCase(updateCreatorCrmAsync.rejected, (state, action) => {
        const key = norm(
          action.payload?.creatorKey ||
            action.meta.arg?.creatorKey ||
            action.meta.arg?.payload?.creatorKey
        );
        const message =
          action.payload?.message ||
          action.error?.message ||
          "Failed to update creator CRM";

        if (key) {
          delete state.savingByKey[key];
          state.updateErrorByKey[key] = message;
        } else {
          state.error = message;
        }
      })

      .addCase(listCreatorCrmActivitiesAsync.pending, (state, action) => {
        const key = norm(action.meta.arg?.creatorKey);
        if (key) {
          state.activitiesLoadingByKey[key] = true;
          delete state.activitiesErrorByKey[key];
        }
      })
      .addCase(listCreatorCrmActivitiesAsync.fulfilled, (state, action) => {
        const key = norm(action.payload?.creatorKey);
        if (!key) return;

        state.activitiesLoadingByKey[key] = false;
        delete state.activitiesErrorByKey[key];
        state.activitiesByKey[key] = Array.isArray(action.payload?.items)
          ? action.payload.items
          : [];
      })
      .addCase(listCreatorCrmActivitiesAsync.rejected, (state, action) => {
        const key = norm(action.payload?.creatorKey || action.meta.arg?.creatorKey);
        const message =
          action.payload?.message ||
          action.error?.message ||
          "Failed to load creator CRM activities";

        if (key) {
          state.activitiesLoadingByKey[key] = false;
          state.activitiesErrorByKey[key] = message;
        } else {
          state.error = message;
        }
      })

      .addCase(createCreatorCrmActivityAsync.pending, (state, action) => {
        const key = extractCreatorKey({
          creatorKey: action.meta.arg?.creatorKey,
          payload: action.meta.arg?.payload,
        });

        if (key) {
          state.creatingActivityByKey[key] = true;
          delete state.activitiesErrorByKey[key];
        }
      })
      .addCase(createCreatorCrmActivityAsync.fulfilled, (state, action) => {
        const key = norm(action.payload?.creatorKey);
        if (!key) return;

        delete state.creatingActivityByKey[key];
        delete state.activitiesErrorByKey[key];
        state.activitiesByKey[key] = prependActivity(
          state.activitiesByKey[key],
          action.payload?.activity
        );
      })
      .addCase(createCreatorCrmActivityAsync.rejected, (state, action) => {
        const key = norm(
          action.payload?.creatorKey ||
            action.meta.arg?.creatorKey ||
            action.meta.arg?.payload?.creatorKey
        );
        const message =
          action.payload?.message ||
          action.error?.message ||
          "Failed to create creator CRM activity";

        if (key) {
          delete state.creatingActivityByKey[key];
          state.activitiesErrorByKey[key] = message;
        } else {
          state.error = message;
        }
      });
  },
});

export const {
  clearCreatorsCrmError,
  clearCreatorCrmUpdateError,
  clearCreatorCrmDetailError,
  clearCreatorCrmActivitiesError,
} = creatorsCrmSlice.actions;

export const selectCreatorsCrmState = (state) =>
  state?.creatorsCrm || initialState;

export const selectCreatorsCrmItems = (state) =>
  selectCreatorsCrmState(state).items || [];

export const selectCreatorsCrmTotal = (state) =>
  Number(selectCreatorsCrmState(state).total || 0);

export const selectCreatorsCrmLoading = (state) =>
  Boolean(selectCreatorsCrmState(state).loading);

export const selectCreatorsCrmError = (state) =>
  selectCreatorsCrmState(state).error || "";

export const selectCreatorsCrmSavingByKey = (state) =>
  selectCreatorsCrmState(state).savingByKey || {};

export const selectCreatorsCrmUpdateErrorsByKey = (state) =>
  selectCreatorsCrmState(state).updateErrorByKey || {};

export const selectCreatorsCrmDetailByKey = (state) =>
  selectCreatorsCrmState(state).detailByKey || {};

export const selectCreatorsCrmDetailLoadingByKey = (state) =>
  selectCreatorsCrmState(state).detailLoadingByKey || {};

export const selectCreatorsCrmDetailErrorByKey = (state) =>
  selectCreatorsCrmState(state).detailErrorByKey || {};

export const selectCreatorsCrmActivitiesByKey = (state) =>
  selectCreatorsCrmState(state).activitiesByKey || {};

export const selectCreatorsCrmActivitiesLoadingByKey = (state) =>
  selectCreatorsCrmState(state).activitiesLoadingByKey || {};

export const selectCreatorsCrmActivitiesErrorByKey = (state) =>
  selectCreatorsCrmState(state).activitiesErrorByKey || {};

export const selectCreatorsCrmCreatingActivityByKey = (state) =>
  selectCreatorsCrmState(state).creatingActivityByKey || {};

export default creatorsCrmSlice.reducer;